import { useQuery } from '@tanstack/react-query';
import { queryDataSource } from '../../api/query';
import { sourceIdBul } from '../../utils/raporSonek';
import { KpiCard, Delta, fmtNum, fmtTRY } from '../yeni-design/primitives';
import type { ReportConfig } from '../../types/panelConfig';
import type { Donem } from './donem'; // CONTROLLER RULING F1: Donem ayri modulde

interface DonemSatiri {
  metrik: string;
  bu_donem: number;
  onceki_donem: number;
  degisim_pct: number;
}

interface KategoriSatiri {
  kategori: string;
  ciro: number;
  miktar: number;
  kar: number;
  pay_yuzde: number;
}

interface KanalSatiri {
  tarih: string;
  kanal_grup: string;
  kanal: string;
  fis_sayisi: number;
  net_ciro: number;
}

interface Props {
  reports: ReportConfig[];
  donem: Donem;
}

/**
 * Katman 1 - ozet serit: dort olcu (net ciro, fis, sepet ortalamasi, brut kar).
 *
 * Sepet ortalamasi K2'nin kapanis sartina gore YALNIZ kanal_grup='Magaza'
 * satirlarindan hesaplanir. "Magaza" kovasi nakit perakendeyi, firmanin
 * kendi online satisini, alti yemek karti operatorunu ve kurumsal
 * musterileri karistirir; yemek karti operatorleri ayda TEK, yuz binlerce
 * liralik toplu fatura keser. Bu "fis"leri sepet ortalamasina katmak
 * ortalamayi sisirir (canli olcum: 381,42 ₺ yerine gercek 331,59 ₺,
 * %15,0 fazla basim).
 */
export function OzetSerit({ reports, donem }: Props) {
  const params = { '@BASTARIH': donem.bas, '@BITTARIH': donem.bit };
  const idDonem = sourceIdBul(reports, 12);
  const idKategori = sourceIdBul(reports, 15);
  // CONTROLLER RULING F4: sonek 35, 995035'in ("Acik Hesaplar", baska bir
  // firma) sonek carpismasi yuzunden 990040'a tasindi ve 990035 PRODUCTIONDAN
  // SILINDI. Ad kontrolu de zorunlu - tutmazsa blok sessizce gizlenir, yanlis
  // firmanin verisini basmaz (bkz. KanalBlok.tsx, ayni desen).
  const idKanal = sourceIdBul(reports, 40, 'Satış Kanalı Özeti');

  // throwOnError: false — burada bir global varsayilani GERI ALMIYOR: Template panelinin
  // main.tsx'i QueryClient'a throwOnError:true VERMEZ (yalniz staleTime/retry ayarli).
  // O desen Yerlice panelinin lib/sorguIstemcisi.ts'inde var, bu panelde degil. Yine de
  // acikca yazildi: niyet kod okuyucuya gorunur olsun — bir kaynak duserse serit tumden
  // cokmesin, o olcu bos kalsin.
  const donemQ = useQuery({
    queryKey: ['sa-ozet-donem', idDonem, params],
    queryFn: () => queryDataSource<DonemSatiri>({ sourceId: idDonem!, parameters: params }),
    enabled: !!idDonem,
    throwOnError: false,
  });
  const kategoriQ = useQuery({
    queryKey: ['sa-ozet-kategori', idKategori, params],
    queryFn: () => queryDataSource<KategoriSatiri>({ sourceId: idKategori!, parameters: params }),
    enabled: !!idKategori,
    throwOnError: false,
  });
  const kanalQ = useQuery({
    queryKey: ['sa-ozet-kanal', idKanal, params],
    queryFn: () => queryDataSource<KanalSatiri>({ sourceId: idKanal!, parameters: params }),
    enabled: !!idKanal,
    throwOnError: false,
  });

  // Bos durum kurali: gerekli hicbir kaynak yoksa serit hic basilmaz.
  if (!idDonem && !idKategori && !idKanal) return null;

  const donemSatir = (metrik: string) =>
    donemQ.data?.data.find((r) => String(r.metrik) === metrik);

  const ciro = donemSatir('Ciro');
  const fis = donemSatir('Fiş');

  // Kaynak eksik/yukleniyor/hatali VEYA hic satir yoksa "veri yok" (null) — sayi
  // basmiyoruz, siblingler gibi '—'. Gercek bir sifir (satir var, kar=0) ise null
  // DEGIL, 0 dondurur ve '₺0' basilir — "veri yok" ile "veri sifir" birbirine karismaz.
  const kategoriSatirlari = kategoriQ.data?.data;
  const kar =
    kategoriSatirlari && kategoriSatirlari.length > 0
      ? kategoriSatirlari.reduce((t, r) => t + Number(r.kar ?? 0), 0)
      : null;

  const magaza = (kanalQ.data?.data ?? [])
    .filter((r) => String(r.kanal_grup) === 'Mağaza')
    .reduce(
      (a, r) => ({
        ciro: a.ciro + Number(r.net_ciro ?? 0),
        fis: a.fis + Number(r.fis_sayisi ?? 0),
      }),
      { ciro: 0, fis: 0 },
    );
  const sepet = magaza.fis > 0 ? magaza.ciro / magaza.fis : null;

  return (
    <div data-testid="blok-ozet" className="grid grid-cols-1 md:grid-cols-4 gap-3">
      <div data-testid="olcu-ciro">
        <KpiCard
          label="Net ciro"
          prefix=""
          value={ciro ? fmtTRY(ciro.bu_donem) : '—'}
          sub={ciro ? <Delta value={ciro.degisim_pct} decimals={2} /> : undefined}
        />
      </div>
      <div data-testid="olcu-fis">
        <KpiCard
          label="Fiş"
          prefix=""
          value={fis ? fmtNum(fis.bu_donem) : '—'}
          sub={fis ? <Delta value={fis.degisim_pct} decimals={2} /> : undefined}
        />
      </div>
      <div data-testid="olcu-sepet">
        {/* fmtTRY kendi ₺ isaretini basar; KpiCard'in varsayilan prefix='₺' burada
            CIFT sembole yol acmasin diye prefix="" ile gecersiz kilindi. */}
        <KpiCard
          label="Sepet ortalaması"
          prefix=""
          value={sepet !== null ? fmtTRY(sepet) : '—'}
          sub="Mağaza kanalı; kurumsal ve yemek kartı faturaları hariç"
        />
      </div>
      <div data-testid="olcu-kar">
        {/* Brut kar bir PARA degeri: fmtNum .toString() kullandigi icin kayan
            nokta hatasini oldugu gibi basiyordu (canlida 1.950.597,7499999998).
            fmtTRY iki haneye yuvarlar; kendi ₺ isaretini bastigi icin prefix=""
            ile KpiCard'in varsayilani gecersiz kilinir (sepet olcusuyle ayni desen). */}
        <KpiCard
          label="Brüt kâr"
          prefix=""
          value={kar !== null ? fmtTRY(kar) : '—'}
        />
      </div>
    </div>
  );
}
